import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, AuthResponse } from '@/types';
import { authService } from '@/services/auth';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: {
    email: string;
    password: string;
    name: string;
    workspace_name?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!authService.getAuthToken();

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('Initializing auth context...');
      try {
        const token = authService.getAuthToken();
        const storedUser = authService.getStoredUser();
        
        console.log('Token exists:', !!token);
        console.log('Stored user exists:', !!storedUser);
        
        if (token && storedUser) {
          console.log('Both token and user exist, verifying with backend...');
          
          try {
            // Verify token is still valid by fetching current user
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            console.log('Token verification successful, user authenticated');
          } catch (error) {
            console.log('Token verification failed, clearing auth state');
            authService.clearAuthData();
            setUser(null);
          }
        } else {
          console.log('No token or user found, clearing auth state');
          authService.clearAuthData();
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authService.clearAuthData();
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log('Auth initialization complete');
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      console.log('Attempting login for:', credentials.email);
      setIsLoading(true);
      const response = await authService.login(credentials);
      console.log('Login successful, setting user state');
      setUser(response.user);
      toast.success('Successfully logged in!');
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.message || 'Login failed';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    name: string;
    workspace_name?: string;
  }) => {
    try {
      console.log('Attempting registration for:', userData.email);
      setIsLoading(true);
      const response = await authService.register(userData);
      console.log('Registration successful, setting user state');
      setUser(response.user);
      toast.success('Account created successfully!');
    } catch (error: any) {
      console.error('Registration failed:', error);
      const errorMessage = error.message || 'Registration failed';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('Attempting logout');
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      toast.success('Logged out successfully');
      
      // Navigate to login page after logout
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      setUser(null);
      toast.success('Logged out successfully');
      
      // Navigate to login page even if logout API fails
      window.location.href = '/login';
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      console.log('Attempting token refresh');
      const response = await authService.refreshToken();
      setUser(response.user);
      console.log('Token refresh successful');
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, log out the user
      authService.clearAuthData();
      setUser(null);
      toast.error('Session expired. Please log in again.');
      window.location.href = '/login';
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
  };

  console.log('Auth context state:', { 
    user: !!user, 
    isAuthenticated, 
    isLoading,
    hasToken: !!authService.getAuthToken()
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 